import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [caseId, setCaseId] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const timer = setInterval(async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setCaseId(data.case_id || null);
          clearInterval(timer);
        } else if (tries.current > 20) {
          setStatus("timeout");
          clearInterval(timer);
        }
      } catch {
        if (tries.current > 20) { setStatus("timeout"); clearInterval(timer); }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAF8] noise-bg flex items-center justify-center px-6">
      <div className="card-warm w-full max-w-md p-10 text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" data-testid="payment-confirming-spinner" />
            <h1 className="font-heading text-2xl font-semibold mt-6">Confirming your payment…</h1>
            <p className="text-sm text-[#5C5C57] mt-2">This takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-[#2C5A2E]" />
            <h1 className="font-heading text-3xl font-semibold mt-6" data-testid="payment-success-heading">You're all set.</h1>
            <p className="text-sm text-[#5C5C57] mt-3">Your evaluation has been created. The next step is your confidential intake.</p>
            <Button data-testid="continue-to-intake-btn"
              onClick={() => navigate(caseId ? `/app/case/${caseId}/intake` : "/app")}
              className="mt-8 w-full rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white h-11 transition-colors">
              Continue to intake
            </Button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <h1 className="font-heading text-2xl font-semibold" data-testid="payment-pending-heading">We're still confirming.</h1>
            <p className="text-sm text-[#5C5C57] mt-3">If your payment completed, your evaluation will appear in your portal shortly.</p>
            <Link to="/app"><Button data-testid="payment-goto-dashboard-btn" className="mt-8 rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">Go to my portal</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
